import {
  FormikErrorMessage,
  Modal,
  Row,
  Section,
  Button,
  TextInput
} from 'shared'
import { Formik, Form, FormikHelpers, FormikProps } from 'formik'
import { Panel, Col, ButtonToolbar, SelectPicker } from 'rsuite'
import { useMemo } from 'react'
import { notifyError, notifySuccess } from 'utils'
import {
  useCreateThresholdsMutation,
  useUpdateThresholdsMutation
} from '../ThresholdsApiSlice'
import {
  THRESHOLDS_FORM_FIELDS,
  IThresholdsForm,
  thresholdsValidationSchema,
  defaultThresholdsFormValues,
  getInitialThresholdsFormValueFromResponse
} from '../utils'
import './CreateThreshold.scss'
import { thresholds, DOCUMENT_TYPES } from '../types'

interface CreateThresholdsProps {
  open: boolean;
  onClose: () => void;
  isEditMode: boolean;
  thresholdsData?: any;
}
const CreateThresholds: React.FC<CreateThresholdsProps> = ({
  open,
  onClose,
  isEditMode,
  thresholdsData
}) => {
  const thresholdsId = thresholdsData?.id
  const initialValues = useMemo(() => {
    const RuleData =
      thresholdsId && thresholdsData
        ? getInitialThresholdsFormValueFromResponse(thresholdsData)
        : defaultThresholdsFormValues
    return RuleData
  }, [thresholdsId, thresholdsData])

  const [createThresholds] = useCreateThresholdsMutation()
  const [editThresholds] = useUpdateThresholdsMutation()

  const documentOptions = [
    { label: 'Payment Remittance', value: DOCUMENT_TYPES.PAYMENT_REMITTANCE },
    {
      label: 'Payment Remittance Line Item',
      value: DOCUMENT_TYPES.PAYMENT_REMITTANCE_LINE_ITEM
    },
    { label: 'Invoice', value: DOCUMENT_TYPES.INVOICE }
  ]

  const renderFormButtons = (formikProps: FormikProps<IThresholdsForm>) => (
    <ButtonToolbar>
      {isEditMode ? (
        <>
          <Button
            className="secondary"
            onClick={() => {
              formikProps.resetForm()
            }}
          >
            Reset
          </Button>
          <Button
            className="primary"
            type="submit"
            disabled={formikProps.isValidating || formikProps.isSubmitting}
          >
            Save changes
          </Button>
        </>
      ) : (
        <>
          <Button
            className="secondary"
            onClick={() => {
              formikProps.resetForm()
            }}
          >
            Reset
          </Button>
          <Button
            className="primary"
            type="submit"
            disabled={formikProps.isValidating || formikProps.isSubmitting}
          >
            Save
          </Button>
        </>
      )}
    </ButtonToolbar>
  )

  const onSubmit = async (
    formValues: IThresholdsForm,
    { setSubmitting }: FormikHelpers<IThresholdsForm>
  ) => {
    const thresholdsDTO: thresholds = {
      fieldKey: formValues.fieldKey,
      minConfidenceScore: formValues.minConfidenceScore,
      documentType: formValues.documentType as unknown as DocumentType
    }
    try {
      if (isEditMode) {
        await editThresholds({
          thresholdsDTO,
          id: thresholdsId
        }).unwrap()

        notifySuccess('Thresholds Update successfully!')
        onClose()
      } else {
        await createThresholds({ thresholdsDTO }).unwrap()
        notifySuccess('Thresholds created successfully!')
        onClose()
      }
    } catch (error) {
      if (isEditMode) {
        notifyError('Failed to Update Thresholds.', {
          containerId: 'modalToast'
        })
      } else {
        notifyError('Failed to create Thresholds.', {
          containerId: 'modalToast'
        })
      }
    }
  }

  return (
    <Modal
      secondary
      open={open}
      onClose={onClose}
      title={isEditMode ? 'Edit Thresholds' : 'Add Thresholds'}
      size="calc(50% - 120px)"
      body={
        <Formik
          initialValues={initialValues}
          validationSchema={thresholdsValidationSchema}
          enableReinitialize
          onSubmit={onSubmit}
        >
          {(formikProps: FormikProps<IThresholdsForm>) => (
            <Form className="create-thresholds-form">
              <Panel>
                <Section title="Thresholds Details">
                  <Row>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={THRESHOLDS_FORM_FIELDS.FIELD_KEY}
                        placeholder="Enter Field Key"
                        dataType="string"
                      />
                      <FormikErrorMessage
                        name={THRESHOLDS_FORM_FIELDS.FIELD_KEY}
                      />
                    </Col>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={THRESHOLDS_FORM_FIELDS.MIN_CONFIDENCE_SCORE}
                        placeholder="Enter Confidence Score"
                        dataType="string"
                      />
                      <FormikErrorMessage
                        name={THRESHOLDS_FORM_FIELDS.MIN_CONFIDENCE_SCORE}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <SelectPicker
                        data={documentOptions}
                        placeholder="Select Document Type"
                        value={
                          formikProps.values[
                            THRESHOLDS_FORM_FIELDS.DOCUMENT_TYPE
                          ]
                        }
                        onChange={(value) =>
                          formikProps.setFieldValue(
                            THRESHOLDS_FORM_FIELDS.DOCUMENT_TYPE,
                            value
                          )
                        }
                        cleanable
                        style={{ width: '100%' }}
                      />
                    </Col>
                  </Row>
                </Section>
              </Panel>
              <Panel className="panel-buttons">
                {renderFormButtons(formikProps)}
              </Panel>
            </Form>
          )}
        </Formik>
      }
    />
  )
}
export default CreateThresholds
